import React, { useMemo, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ensureFontsLoaded } from "../../utils/fontLoader";

// Font lists (same as FontSelector)
const SYSTEM_FONTS = [
  "Arial",
  "Helvetica",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Georgia",
  "Palatino",
  "Garamond",
  "Comic Sans MS",
  "Trebuchet MS",
  "Impact",
  "Lucida Console",
  "Tahoma",
];

const TYPEFACE_FONTS = [
  "Almarai",
  "Anton",
  "Barlow Condensed",
  "BBH Bartle",
  "Cairo",
  "Caveat",
  "Dancing Script",
  "Edu SA Beginner",
  "Follow",
  "Great Vibes",
  "Josefin Sans Thin",
  "Lobster",
  "Lobster Two",
  "Noto Sans Symbols",
  "Orbitron",
  "Pacifico",
  "Satisfy",
  "Shadows Into Light",
  "Smooch Sans Thin",
  "Space Grotesk Light",
  "Tajawal",
];

const GOOGLE_FONTS = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Oswald",
  "Raleway",
  "Poppins",
  "Source Sans Pro",
  "Playfair Display",
  "Merriweather",
  "Ubuntu",
  "Bebas Neue",
  "Bangers",
  "Righteous",
  "Fredoka One",
  "Comfortaa",
  "Quicksand",
  "Kalam",
  "Permanent Marker",
  "Indie Flower",
  "Crimson Text",
  "Fira Sans",
  "PT Sans",
  "Nunito",
  "Work Sans",
  "Rubik",
  "Noto Sans",
  "Mukta",
  "DM Sans",
  "Manrope",
  "Outfit",
  "Plus Jakarta Sans",
  "Trirong",
  // Arabic Fonts
  "Amiri",
  "Changa",
  "El Messiri",
  "Harmattan",
  "Lateef",
  "Markazi Text",
  "Reem Kufi",
  "Scheherazade New",
  "Noto Sans Arabic",
  "Noto Naskh Arabic",
  "Noto Kufi Arabic",
  "IBM Plex Sans Arabic",
  "Cairo",
  "Tajawal",
  "Almarai",
];

// Combine all fonts and remove duplicates
const getAllFonts = (): string[] => {
  const allFonts = [...SYSTEM_FONTS, ...GOOGLE_FONTS, ...TYPEFACE_FONTS];
  return Array.from(new Set(allFonts)).sort();
};

// Sanitize font name for use in CSS classes (replace spaces and special chars with dashes)
const sanitizeFontName = (fontName: string): string => {
  return fontName.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
};

// Create a font name map: sanitized -> original
const createFontMap = (fonts: string[]): Map<string, string> => {
  const map = new Map<string, string>();
  fonts.forEach((font) => {
    map.set(sanitizeFontName(font), font);
  });
  return map;
};

const extractFontsFromHtml = (
  html: string,
  fontMap: Map<string, string>
): string[] => {
  if (!html) return [];

  const matches = html.match(/ql-font-([a-z0-9-]+)/gi) || [];
  const sanitized = matches
    .map((m) => m.replace(/^ql-font-/i, "").toLowerCase())
    .filter(Boolean);

  const families = sanitized
    .map((key) => fontMap.get(key))
    .filter((v): v is string => typeof v === "string" && v.length > 0);

  return Array.from(new Set(families));
};

// Comprehensive color palette for text and background colors
const COLOR_PALETTE = [
  // Grayscale
  "#000000", // Black
  "#1a1a1a", // Very Dark Gray
  "#333333", // Dark Gray
  "#4a4a4a", // Medium Dark Gray
  "#666666", // Medium Gray
  "#808080", // Gray
  "#999999", // Light Gray
  "#b3b3b3", // Lighter Gray
  "#cccccc", // Very Light Gray
  "#e6e6e6", // Almost White
  "#f5f5f5", // Off White
  "#ffffff", // White
  
  // Reds
  "#dc2626", // Red 600
  "#ef4444", // Red 500
  "#f87171", // Red 400
  "#fca5a5", // Red 300
  "#fee2e2", // Red 200
  "#991b1b", // Red 800
  "#7f1d1d", // Red 900
  
  // Oranges
  "#ea580c", // Orange 600
  "#f97316", // Orange 500
  "#fb923c", // Orange 400
  "#fdba74", // Orange 300
  "#fed7aa", // Orange 200
  "#c2410c", // Orange 800
  "#9a3412", // Orange 900
  
  // Yellows
  "#ca8a04", // Yellow 600
  "#eab308", // Yellow 500
  "#facc15", // Yellow 400
  "#fde047", // Yellow 300
  "#fef08a", // Yellow 200
  "#a16207", // Yellow 800
  "#854d0e", // Yellow 900
  
  // Greens
  "#16a34a", // Green 600
  "#22c55e", // Green 500
  "#4ade80", // Green 400
  "#86efac", // Green 300
  "#bbf7d0", // Green 200
  "#15803d", // Green 800
  "#166534", // Green 900
  
  // Teals
  "#0d9488", // Teal 600
  "#14b8a6", // Teal 500
  "#2dd4bf", // Teal 400
  "#5eead4", // Teal 300
  "#99f6e4", // Teal 200
  "#0f766e", // Teal 800
  "#115e59", // Teal 900
  
  // Blues
  "#2563eb", // Blue 600
  "#3b82f6", // Blue 500
  "#60a5fa", // Blue 400
  "#93c5fd", // Blue 300
  "#bfdbfe", // Blue 200
  "#1d4ed8", // Blue 800
  "#1e40af", // Blue 900
  "#1e3a8a", // Blue 900 (darker)
  
  // Indigos
  "#4f46e5", // Indigo 600
  "#6366f1", // Indigo 500
  "#818cf8", // Indigo 400
  "#a5b4fc", // Indigo 300
  "#c7d2fe", // Indigo 200
  "#4338ca", // Indigo 800
  "#312e81", // Indigo 900
  
  // Purples
  "#9333ea", // Purple 600
  "#a855f7", // Purple 500
  "#c084fc", // Purple 400
  "#d8b4fe", // Purple 300
  "#e9d5ff", // Purple 200
  "#7e22ce", // Purple 800
  "#6b21a8", // Purple 900
  
  // Pinks
  "#db2777", // Pink 600
  "#ec4899", // Pink 500
  "#f472b6", // Pink 400
  "#f9a8d4", // Pink 300
  "#fce7f3", // Pink 200
  "#be185d", // Pink 800
  "#9f1239", // Pink 900
  
  // Browns
  "#92400e", // Brown 600
  "#a16207", // Brown 500
  "#ca8a04", // Brown 400
  "#d97706", // Brown 300
  "#f59e0b", // Brown 200
  "#78350f", // Brown 800
  "#451a03", // Brown 900
  
  // Special colors
  "#ff0000", // Pure Red
  "#00ff00", // Pure Green
  "#0000ff", // Pure Blue
  "#ffff00", // Pure Yellow
  "#ff00ff", // Magenta
  "#00ffff", // Cyan
  "#ffa500", // Orange
  "#800080", // Purple
  "#ffc0cb", // Pink
  "#a52a2a", // Brown
];

export interface RichTextEditorProps {
 value: string;
 onChange: (value: string) => void;
 label?: string;
 error?: string;
 helperText?: string;
 placeholder?: string;
 required?: boolean;
 className?: string;
 height?: string;
 isLoading?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
 value,
 onChange,
 label,
 error,
 helperText,
 placeholder = "Enter content...",
 required = false,
 className = "",
 height = "300px",
 isLoading = false,
}) => {
 const editorId = useMemo(
  () => `rich-text-editor-${Math.random().toString(36).substr(2, 9)}`,
  []
 );
 const wrapperRef = useRef<HTMLDivElement>(null);
 const quillRef = useRef<any>(null);
 const fontsRegisteredRef = useRef(false);

 const hasError = !!error;

 // Get all available fonts
 const allFonts = useMemo(() => getAllFonts(), []);
 const fontMap = useMemo(() => createFontMap(allFonts), [allFonts]);
 const sanitizedFonts = useMemo(() => allFonts.map(sanitizeFontName), [allFonts]);

 // Register fonts with Quill (run once, before editor initialization)
 useEffect(() => {
  if (fontsRegisteredRef.current) return;
  
  // Wait for Quill to be available
  const registerFonts = () => {
   try {
    // Access Quill from ReactQuill or window
    const Quill = (ReactQuill as any).Quill || (window as any).Quill;
    if (Quill && Quill.import) {
     const Font = Quill.import("formats/font");
     if (Font) {
      // Override the whitelist with sanitized font names
      // This ensures CSS classes are valid (no spaces)
      Font.whitelist = sanitizedFonts;
      
      // Override Quill's Picker class to customize font label formatting
      const Picker = Quill.import("ui/picker");
      if (Picker) {
       // Store original selectLabel method
       const originalSelectLabel = Picker.prototype.selectLabel;
       
       // Override selectLabel to remove "Sans Serif" prefix from font names
       Picker.prototype.selectLabel = function(value: string, defaultLabel: string): string {
        // If this is the font picker, format the label without "Sans Serif" prefix
        if (this.label && this.label.classList && this.label.classList.contains("ql-font")) {
         if (value && fontMap.has(value)) {
          const fontName = fontMap.get(value);
          if (fontName) {
           // Remove any "Sans Serif" prefix and return clean font name
           return fontName.replace(/^Sans Serif\s+/i, "").trim() || fontName;
          }
         }
         // If value is not in map, try to clean it anyway
         if (value) {
          return value.replace(/^Sans Serif\s+/i, "").trim() || value;
         }
         return defaultLabel || "Sans Serif";
        }
        // For other pickers, use original behavior
        return originalSelectLabel ? originalSelectLabel.call(this, value, defaultLabel) : defaultLabel;
       };
      }
      
      // Re-register the Font format with the sanitized whitelist
      Quill.register(Font, true);
      fontsRegisteredRef.current = true;
      return true;
     }
    }
   } catch (error) {
    console.warn("Error registering fonts with Quill:", error);
   }
   return false;
  };

  // Try immediately
  if (!registerFonts()) {
   // If Quill is not available yet, wait a bit and try again
   const timeout = setTimeout(() => {
    registerFonts();
   }, 100);
   return () => clearTimeout(timeout);
  }
  return undefined;
 }, [sanitizedFonts, fontMap]);

 // Ensure fonts used by current content are loaded (on demand)
 useEffect(() => {
  const fontsInContent = extractFontsFromHtml(value, fontMap);
  if (fontsInContent.length === 0) return;
  ensureFontsLoaded(fontsInContent).catch(() => {
   // Non-fatal: editor should continue rendering with fallbacks
  });
 }, [value, fontMap]);

 const modules = useMemo(
  () => ({
   toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: sanitizedFonts }], // Use sanitized font list for valid CSS classes
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
     { list: "ordered" },
     { list: "bullet" },
     { indent: "-1" },
     { indent: "+1" },
    ],
    ["link", "image", "video"],
    [{ color: COLOR_PALETTE }, { background: COLOR_PALETTE }], // Use custom color palette
    [{ align: [] }],
    ["clean"],
   ],
   clipboard: {
    matchVisual: false,
   },
  }),
  [sanitizedFonts]
 );

 const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "color",
  "background",
  "align",
 ];

 // Memoize CSS generation to avoid regenerating on every render
 const editorStyles = useMemo(() => {
  const fontStyles = allFonts
   .map((font) => {
    const fontValue = sanitizeFontName(font);
    return `
     .rich-text-editor-wrapper .ql-picker.ql-font .ql-picker-item[data-value="${fontValue}"],
     .rich-text-editor-wrapper .ql-picker.ql-font .ql-picker-label[data-value="${fontValue}"] {
      font-family: "${font}", sans-serif !important;
     }
     .rich-text-editor-wrapper .ql-editor .ql-font-${fontValue} {
      font-family: "${font}", sans-serif !important;
     }
    `;
   })
   .join("");

  return `
   .rich-text-editor-wrapper {
    min-height: ${height} !important;
    display: flex;
    flex-direction: column;
   }
   .rich-text-editor-wrapper .ql-container {
    border: none !important;
    border-top: 1px solid ${hasError ? "#ef4444" : "#d1d5db"} !important;
    font-size: 14px;
    flex: 1;
    min-height: calc(${height} - 48px) !important;
   }
   .rich-text-editor-wrapper .ql-editor {
    padding: 12px 15px;
    min-height: calc(${height} - 48px) !important;
   }
   .rich-text-editor-wrapper .ql-editor.ql-blank::before {
    color: #9ca3af;
    font-style: normal;
   }
   .rich-text-editor-wrapper .ql-toolbar {
    border: none !important;
    border-bottom: 1px solid ${hasError ? "#ef4444" : "#d1d5db"} !important;
    padding: 8px;
    background-color: #f9fafb;
   }
   .rich-text-editor-wrapper .ql-toolbar .ql-stroke {
    stroke: #374151;
   }
   .rich-text-editor-wrapper .ql-toolbar .ql-fill {
    fill: #374151;
   }
   .rich-text-editor-wrapper .ql-toolbar button:hover,
   .rich-text-editor-wrapper .ql-toolbar button.ql-active {
    color: #4f46e5;
   }
   .rich-text-editor-wrapper .ql-toolbar button:hover .ql-stroke,
   .rich-text-editor-wrapper .ql-toolbar button.ql-active .ql-stroke {
    stroke: #4f46e5;
   }
   .rich-text-editor-wrapper .ql-toolbar button:hover .ql-fill,
   .rich-text-editor-wrapper .ql-toolbar button.ql-active .ql-fill {
    fill: #4f46e5;
   }
   .rich-text-editor-wrapper:focus-within {
    outline: 2px solid ${hasError ? "#ef4444" : "#4f46e5"};
    outline-offset: 2px;
   }
   /* Style font dropdown to show fonts with their actual font families */
   .rich-text-editor-wrapper .ql-picker.ql-font .ql-picker-item {
    font-size: 14px;
    padding: 4px 8px;
   }
   ${fontStyles}
   /* Fix color picker popup positioning to prevent cutoff */
   .rich-text-editor-wrapper .ql-picker.ql-color,
   .rich-text-editor-wrapper .ql-picker.ql-background {
    position: static !important;
   }
   .rich-text-editor-wrapper .ql-picker.ql-color .ql-picker-label,
   .rich-text-editor-wrapper .ql-picker.ql-background .ql-picker-label {
    position: relative;
   }
   /* Use fixed positioning for color picker dropdowns to escape container overflow */
   .rich-text-editor-wrapper .ql-picker.ql-color .ql-picker-options,
   .rich-text-editor-wrapper .ql-picker.ql-background .ql-picker-options {
    z-index: 10000 !important;
    padding: 5px;
    width: 152px;
    background: white;
    border: 1px solid #ccc;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
   }
   /* Ensure toolbar and pickers allow overflow */
   .rich-text-editor-wrapper .ql-toolbar {
    position: relative;
    overflow: visible !important;
   }
   .rich-text-editor-wrapper .ql-toolbar .ql-picker {
    position: static !important;
    overflow: visible !important;
   }
   /* Make sure the color picker dropdown can extend beyond container */
   .rich-text-editor-wrapper {
    overflow: visible !important;
   }
   .rich-text-editor-wrapper .ql-container {
    overflow: visible !important;
   }
   /* Color swatch styling */
   .rich-text-editor-wrapper .ql-picker.ql-color .ql-picker-item,
   .rich-text-editor-wrapper .ql-picker.ql-background .ql-picker-item {
    width: 17px;
    height: 17px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    margin: 2px;
    display: inline-block;
    cursor: pointer;
    border-radius: 2px;
   }
   .rich-text-editor-wrapper .ql-picker.ql-color .ql-picker-item:hover,
   .rich-text-editor-wrapper .ql-picker.ql-background .ql-picker-item:hover {
    border-color: #4f46e5;
    box-shadow: 0 0 0 1px #4f46e5;
   }
  `;
 }, [height, hasError, allFonts]);

 useEffect(() => {
  if (wrapperRef.current) {
   const editor = wrapperRef.current.querySelector(".ql-editor") as HTMLElement;
   const container = wrapperRef.current.querySelector(".ql-container") as HTMLElement;
   if (editor) {
    editor.style.minHeight = height;
   }
   if (container) {
    container.style.minHeight = height;
   }
  }
 }, [height]);

 // Update placeholder when it changes
 useEffect(() => {
  if (quillRef.current) {
   const quill = quillRef.current.getEditor();
   if (quill && quill.root) {
    quill.root.setAttribute("data-placeholder", placeholder);
   }
  } else if (wrapperRef.current) {
   // Fallback: update via DOM if ref not available yet
   const editor = wrapperRef.current.querySelector(".ql-editor") as HTMLElement;
   if (editor) {
    editor.setAttribute("data-placeholder", placeholder);
   }
  }
 }, [placeholder]);

 // Customize font picker to show original font names
 useEffect(() => {
  if (!wrapperRef.current) return;

  let updateTimeout: NodeJS.Timeout | null = null;
  let isUpdating = false;

  const updateFontPickerLabels = () => {
   // Prevent multiple simultaneous updates
   if (isUpdating) return;
   isUpdating = true;

   requestAnimationFrame(() => {
    try {
     const fontPicker = wrapperRef.current?.querySelector(".ql-picker.ql-font");
     if (!fontPicker) {
      isUpdating = false;
      return;
     }

     // Helper function to get clean font name without "Sans Serif" prefix
     const getCleanFontName = (fontValue: string | null): string => {
      if (!fontValue) return "Sans Serif";
      
      // First, try to get the original font name from the map
      let fontName: string | undefined;
      if (fontMap.has(fontValue)) {
       fontName = fontMap.get(fontValue);
      }
      
      // Use the font name from map, or fall back to the value itself
      const nameToClean = fontName || fontValue;
      
      // Remove "Sans Serif" prefix (case-insensitive, with optional whitespace)
      let cleaned = nameToClean.replace(/^Sans Serif\s+/i, "").trim();
      
      // Also remove if "Sans Serif" appears at the start with different spacing
      cleaned = cleaned.replace(/^Sans\s*Serif\s+/i, "").trim();
      
      // If after cleaning we have nothing, return the original
      return cleaned || nameToClean;
     };

     // Update label - always set to clean name
     const label = fontPicker.querySelector(".ql-picker-label") as HTMLElement;
     if (label) {
      const currentValue = label.getAttribute("data-value");
      const cleanName = getCleanFontName(currentValue);
      // Force update to clean name
      if (label.textContent !== cleanName) {
       label.textContent = cleanName;
      }
     }

     // Update all picker items - always set to clean name
     const items = fontPicker.querySelectorAll(".ql-picker-item");
     items.forEach((item) => {
      const value = item.getAttribute("data-value");
      const cleanName = getCleanFontName(value);
      const itemElement = item as HTMLElement;
      // Force update to clean name
      if (itemElement.textContent !== cleanName) {
       itemElement.textContent = cleanName;
      }
     });
    } finally {
     isUpdating = false;
    }
   });
  };

  // Debounced update function
  const debouncedUpdate = () => {
   if (updateTimeout) {
    clearTimeout(updateTimeout);
   }
   updateTimeout = setTimeout(updateFontPickerLabels, 50);
  };

  // Update after a short delay to ensure Quill has initialized
  const initialTimeout = setTimeout(updateFontPickerLabels, 200);
  
  // Also set up a periodic check to keep labels clean (in case Quill overwrites them)
  const intervalId = setInterval(() => {
   updateFontPickerLabels();
  }, 1000); // Check every second

  // Also update when font picker changes - observe both attributes and text content
  const fontPicker = wrapperRef.current.querySelector(".ql-picker.ql-font");
  if (fontPicker) {
   const observer = new MutationObserver((mutations) => {
    // Update if data-value attributes changed OR if text content changed
    const hasRelevantChange = mutations.some(
     (mutation) =>
      (mutation.type === "attributes" &&
       mutation.attributeName === "data-value") ||
      (mutation.type === "childList" || mutation.type === "characterData")
    );
    if (hasRelevantChange) {
     debouncedUpdate();
    }
   });

   observer.observe(fontPicker, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-value"],
    characterData: true,
   });

   // Listen for clicks on the font picker
   const handleClick = () => {
    setTimeout(updateFontPickerLabels, 10);
   };
   fontPicker.addEventListener("click", handleClick);
   
   // Also listen for when the dropdown opens
   const handleMouseDown = () => {
    setTimeout(updateFontPickerLabels, 50);
   };
   fontPicker.addEventListener("mousedown", handleMouseDown);

   return () => {
    clearTimeout(initialTimeout);
    clearInterval(intervalId);
    if (updateTimeout) {
     clearTimeout(updateTimeout);
    }
    observer.disconnect();
    fontPicker.removeEventListener("click", handleClick);
    fontPicker.removeEventListener("mousedown", handleMouseDown);
   };
  }

  return () => {
   clearTimeout(initialTimeout);
   clearInterval(intervalId);
   if (updateTimeout) {
    clearTimeout(updateTimeout);
   }
  };
 }, [fontMap]);

 // Fix color picker positioning to prevent cutoff
 useEffect(() => {
  if (!wrapperRef.current) return;

  let positionTimeout: NodeJS.Timeout | null = null;
  let isPositioning = false;

  const positionColorPickers = () => {
   // Prevent multiple simultaneous positioning calls
   if (isPositioning) return;
   isPositioning = true;

   requestAnimationFrame(() => {
    try {
     const colorPickers = wrapperRef.current?.querySelectorAll(
      ".ql-picker.ql-color, .ql-picker.ql-background"
     );
     
     if (!colorPickers || colorPickers.length === 0) {
      isPositioning = false;
      return;
     }

     colorPickers.forEach((picker) => {
      const options = picker.querySelector(".ql-picker-options") as HTMLElement;
      if (!options) return;

      const label = picker.querySelector(".ql-picker-label") as HTMLElement;
      if (!label) return;

      // Check if dropdown is expanded
      if (options.classList.contains("ql-expanded")) {
       const labelRect = label.getBoundingClientRect();
       const optionsRect = options.getBoundingClientRect();
       const viewportWidth = window.innerWidth;
       const viewportHeight = window.innerHeight;

       // Calculate position
       let top = labelRect.bottom + window.scrollY;
       let left = labelRect.left + window.scrollX;

       // Adjust if would go off right edge
       if (left + optionsRect.width > viewportWidth) {
        left = viewportWidth - optionsRect.width - 10;
       }

       // Adjust if would go off bottom edge (show above instead)
       if (top + optionsRect.height > viewportHeight + window.scrollY) {
        top = labelRect.top + window.scrollY - optionsRect.height;
       }

       // Ensure minimum spacing from edges
       if (left < 10) left = 10;
       if (top < 10) top = 10;

       // Apply fixed positioning to escape container overflow
       options.style.position = "fixed";
       options.style.top = `${top}px`;
       options.style.left = `${left}px`;
       options.style.zIndex = "10000";
      } else {
       // Reset position when collapsed to allow Quill's default behavior
       options.style.position = "";
       options.style.top = "";
       options.style.left = "";
       options.style.zIndex = "";
      }
     });
    } finally {
     isPositioning = false;
    }
   });
  };

  // Debounced positioning function
  const debouncedPosition = () => {
   if (positionTimeout) {
    clearTimeout(positionTimeout);
   }
   positionTimeout = setTimeout(positionColorPickers, 10);
  };

  // Only observe color picker options, not the entire subtree
  const colorPickers = wrapperRef.current.querySelectorAll(
   ".ql-picker.ql-color, .ql-picker.ql-background"
  );

  const observers: MutationObserver[] = [];

  colorPickers.forEach((picker) => {
   const options = picker.querySelector(".ql-picker-options");
   if (options) {
    const observer = new MutationObserver((mutations) => {
     // Only react to class changes on the options element
     const hasClassChange = mutations.some(
      (mutation) =>
       mutation.type === "attributes" &&
       mutation.attributeName === "class" &&
       mutation.target === options
     );
     if (hasClassChange) {
      debouncedPosition();
     }
    });

    observer.observe(options, {
     attributes: true,
     attributeFilter: ["class"],
    });
    observers.push(observer);
   }
  });

  // Listen for clicks on color pickers
  const handleClick = (e: MouseEvent) => {
   const target = e.target as HTMLElement;
   if (
    target.closest(".ql-picker.ql-color") ||
    target.closest(".ql-picker.ql-background")
   ) {
    // Delay to allow Quill to process the click and toggle ql-expanded class
    setTimeout(positionColorPickers, 50);
   }
  };

  document.addEventListener("click", handleClick, true);

  return () => {
   if (positionTimeout) {
    clearTimeout(positionTimeout);
   }
   observers.forEach((observer) => observer.disconnect());
   document.removeEventListener("click", handleClick, true);
  };
 }, []);
 if (isLoading) {
  return (
   <div className={className}>
    {label && (
     <div className="h-4 bg-gray-200 rounded w-24 mb-1 animate-pulse" />
    )}
    <div className="border border-gray-300 overflow-hidden">
     <div className="h-12 bg-gray-100 animate-pulse" />
     <div className="h-64 bg-gray-50 animate-pulse" style={{ minHeight: height }} />
    </div>
   </div>
  );
 }
 return (
  <div className={className}>
   {label && (
    <label
     htmlFor={editorId}
     className="block text-sm font-medium text-gray-700 mb-1"
    >
     {label}
     {required && <span className="text-red-500 ml-1">*</span>}
    </label>
   )}
   <div
    ref={wrapperRef}
    className={`rich-text-editor-wrapper ${
     hasError ? "border-red-500" : "border-gray-300"
    } overflow-hidden flex flex-col`}
    style={{ border: "1px solid", minHeight: height }}
   >
    <ReactQuill
     ref={quillRef}
     theme="snow"
     value={value}
     onChange={onChange}
     modules={modules}
     formats={formats}
     placeholder={placeholder}
    />
    <style>{editorStyles}</style>
   </div>
   {error && (
    <p className="mt-1 text-sm text-red-600" role="alert">
     {error}
    </p>
   )}
   {helperText && !error && (
    <p className="mt-1 text-sm text-gray-500">{helperText}</p>
   )}
  </div>
 );
};

export default RichTextEditor;

